import React, { useEffect, useState } from 'react';
import { FiTrash2 } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import { setModalState, closeModalWithResult } from '../../store/modalSlice';
import { deleteSchedule } from '../../store/agentScheduleSlice';
import { deleteConversation } from '../../store/aiChatSlice';
import { deleteProcessedModelSchemaFunc } from '../../store/processedModelSchemaSlice';
import { deleteProject } from '../../store/projectSlice';
import { deleteDatasetAction } from '../../store/datasetSlice';
import { ShareService } from '../../services/shareService';
import ModalBase from './ModalBase';
import type { AppDispatch, RootState } from '../../store';

interface GeneralDeleteModalProps {
  onClose: (result?: any) => void;
  title?: string;
  itemName: string;
  itemId?: string;
  itemType?: 'conversation' | 'schedule' | 'processedModelSchema' | 'project' | 'dataset';
  cancelView?: string;
  cancelProperties?: any;
  confirmView?: string;
  confirmProperties?: any;
}

const GeneralDeleteModal: React.FC<GeneralDeleteModalProps> = ({
  title = 'Delete',
  itemName,
  itemId,
  itemType,
  cancelView,
  cancelProperties,
  confirmView,
  confirmProperties,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [isDeleting, setIsDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Live share links tied to this schedule's runners — deleting a schedule doesn't kill them automatically; the checkbox lets the user cascade-deactivate (default on).
  const [activeShareCount, setActiveShareCount] = useState(0);
  const [deactivateShares, setDeactivateShares] = useState(true);

  const schedule = useSelector((state: RootState) =>
    itemType === 'schedule' && itemId
      ? state.agentSchedule.schedules.find((s) => s.id === itemId)
      : undefined
  );

  useEffect(() => {
    setErrorMessage(null);
  }, [itemId]);

  useEffect(() => {
    setActiveShareCount(0);
    if (!schedule) return;
    const runnerIds = (schedule.runners?.items || []).map((r) => r.id);
    if (!runnerIds.length) return;
    let cancelled = false;
    ShareService.listActiveShareIdsForRunners(runnerIds).then((ids) => {
      if (!cancelled) setActiveShareCount(ids.length);
    });
    return () => {
      cancelled = true;
    };
  }, [schedule]);

  const handleCancel = () => {
    if (isDeleting) return;
    if (cancelView) {
      dispatch(setModalState({ modalOpen: true, modalView: cancelView, properties: cancelProperties ?? null }));
    } else {
      dispatch(closeModalWithResult({ confirmed: false }));
    }
  };

  const handleConfirm = async () => {
    setIsDeleting(true);
    setErrorMessage(null);
    try {
      if (itemId && itemType) {
        if (itemType === 'conversation') {
          await dispatch(deleteConversation(itemId)).unwrap();
        } else if (itemType === 'schedule') {
          await dispatch(deleteSchedule({ id: itemId, deactivateShares })).unwrap();
        } else if (itemType === 'processedModelSchema') {
          await dispatch(deleteProcessedModelSchemaFunc({ id: itemId })).unwrap();
        } else if (itemType === 'project') {
          await dispatch(deleteProject(itemId)).unwrap();
        } else if (itemType === 'dataset') {
          await dispatch(deleteDatasetAction({ id: itemId })).unwrap();
        }
      }

      if (confirmView) {
        dispatch(setModalState({ modalOpen: true, modalView: confirmView, properties: confirmProperties ?? null }));
      } else {
        dispatch(closeModalWithResult({ confirmed: true, itemId, itemType }));
      }
    } catch {
      setErrorMessage('Something went wrong. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    // Same frame and copy as DeleteApprove (group, asset, p-curve) — one delete modal shape across the app, so this one only adds what those don't have: the share-link opt-out.
    <ModalBase
      title={title}
      icon={<FiTrash2 className="h-5 w-5 text-eai-danger-text" />}
      onClose={handleCancel}
      size="sm"
      footer={
        <>
          <button
            className="eai-btn-outline disabled:cursor-not-allowed disabled:opacity-50"
            type="button"
            disabled={isDeleting}
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="eai-btn-danger disabled:cursor-not-allowed disabled:opacity-70"
            type="button"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting…' : 'Delete'}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-2">
        <p className="eai-panel-title">
          Are you sure you want to permanently delete {itemName}?
        </p>
        <p className="font-inter text-eai-body-sm text-eai-danger-text">
          This action can not be undone!
        </p>

        {itemType === 'schedule' && activeShareCount > 0 && (
          <label className="mt-1 flex cursor-pointer items-start gap-2 text-left">
            <input
              type="checkbox"
              checked={deactivateShares}
              onChange={(e) => setDeactivateShares(e.target.checked)}
              className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-eai-cta-primary"
            />
            <span className="eai-description-sm">
              Also turn off {activeShareCount} public share {activeShareCount === 1 ? 'link' : 'links'} for
              this schedule&apos;s agents. If unchecked, the {activeShareCount === 1 ? 'link keeps' : 'links keep'}{' '}
              showing the last result.
            </span>
          </label>
        )}

        {errorMessage && (
          <p className="font-inter text-eai-body-sm text-eai-danger-text">{errorMessage}</p>
        )}
      </div>
    </ModalBase>
  );
};

export default GeneralDeleteModal;
